// cspell:ignore firestore Firestore
// src/AdminDashboard.js

import React, { useState, useEffect, useCallback, useContext } from "react";
import { auth } from "./firebase-config";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { RestaurantContext } from "./contexts/RestaurantContext";
import { menuService } from "./services/menuService";
import CategoryManager from "./components/CategoryManager";
import MigrationDashboard from "./components/MigrationDashboard";
import APIKeyManager from "./components/APIKeyManager";
import PhaseOneTwoDashboard from "./components/Phase1_2Dashboard";

function AdminDashboard() {
  const [drinks, setDrinks] = useState([]);
  const [newDrink, setNewDrink] = useState({
    name: "",
    price: "",
    description: "",
    category: "",
  });
  const navigate = useNavigate();
  const { restaurant: currentRestaurant } = useContext(RestaurantContext);

  // Category management state
  const [managingCategory, setManagingCategory] = useState(null);

  // Migration dashboard state
  const [showMigrationDashboard, setShowMigrationDashboard] = useState(false);

  // API Key manager state
  const [showAPIKeyManager, setShowAPIKeyManager] = useState(false);

  // Phase 1.2 dashboard state
  const [showPhaseOneTwoDashboard, setShowPhaseOneTwoDashboard] = useState(false);

  // NEW: State to track which drink is being edited
  const [editingDrinkId, setEditingDrinkId] = useState(null);
  const [editingDrinkData, setEditingDrinkData] = useState({
    price: "",
    description: "",
  });

  // Fetch all drinks for management
  useEffect(() => {
    if (!currentRestaurant?.id) {
      setDrinks([]);
      return;
    }

    // Use the tenant-aware menu service to fetch items
    const unsubscribe = menuService.subscribeToMenuItems(
      currentRestaurant.id,
      (items) => {
        // Filter to only show actual menu items (not headers)
        const menuItems = items.filter(
          (item) => item.type === "item" || !item.type
        );
        setDrinks(menuItems);
        console.log("Admin Dashboard received drinks:", menuItems);
      },
      (error) => {
        console.error("Error fetching menu items:", error);
        setDrinks([]);
      }
    );

    return () => unsubscribe && unsubscribe();
  }, [currentRestaurant?.id]);

  const handleAddDrink = useCallback(
    async (e) => {
      e.preventDefault();
      if (
        !newDrink.name ||
        !newDrink.price ||
        !newDrink.category ||
        !currentRestaurant?.id
      )
        return;

      try {
        await menuService.createMenuItem(currentRestaurant.id, {
          ...newDrink,
          price: parseFloat(newDrink.price),
          inStock: true,
          type: "item",
        });
        setNewDrink({ name: "", price: "", description: "", category: "" });
      } catch (error) {
        console.error("Error adding drink:", error);
        alert("Failed to add drink. Please try again.");
      }
    },
    [newDrink, currentRestaurant?.id]
  );

  const handleUpdateStock = useCallback(
    async (id, currentStock) => {
      if (!currentRestaurant?.id) return;

      try {
        await menuService.updateMenuItem(currentRestaurant.id, id, {
          inStock: !currentStock,
        });
      } catch (error) {
        console.error("Error updating stock:", error);
        alert("Failed to update stock. Please try again.");
      }
    },
    [currentRestaurant?.id]
  );

  const handleDeleteDrink = useCallback(
    async (id) => {
      if (!currentRestaurant?.id) return;

      if (window.confirm("Are you sure you want to delete this drink?")) {
        try {
          await menuService.deleteMenuItem(currentRestaurant.id, id);
        } catch (error) {
          console.error("Error deleting drink:", error);
          alert("Failed to delete drink. Please try again.");
        }
      }
    },
    [currentRestaurant?.id]
  );

  const handleLogout = useCallback(async () => {
    await signOut(auth);
    navigate("/login");
  }, [navigate]);

  // NEW: Function to start editing a drink
  const handleEditClick = useCallback((drink) => {
    setEditingDrinkId(drink.id);
    setEditingDrinkData({ price: drink.price, description: drink.description });
  }, []);

  // NEW: Function to cancel editing
  const handleCancelEdit = useCallback(() => {
    setEditingDrinkId(null);
    setEditingDrinkData({ price: "", description: "" });
  }, []);

  // NEW: Function to save the updated drink details
  const handleUpdateDrink = useCallback(
    async (id) => {
      if (!currentRestaurant?.id) return;

      try {
        await menuService.updateMenuItem(currentRestaurant.id, id, {
          price: parseFloat(editingDrinkData.price),
          description: editingDrinkData.description,
        });
        handleCancelEdit(); // Reset the form after saving
      } catch (error) {
        console.error("Error updating drink:", error);
        alert("Failed to update drink. Please try again.");
      }
    },
    [editingDrinkData, handleCancelEdit, currentRestaurant?.id]
  );

  return (
    <div className="admin-dashboard">
      <h1>Admin Dashboard - {currentRestaurant?.name || "Loading..."}</h1>

      {!currentRestaurant && (
        <div
          style={{
            padding: "1rem",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffeaa7",
            borderRadius: "0.5rem",
            marginBottom: "1rem",
          }}
        >
          <p>No restaurant selected or loading...</p>
        </div>
      )}
      <button onClick={handleLogout} className="logout-button">
        Logout
      </button>

      {/* Navigation section */}
      <div
        className="admin-navigation"
        style={{
          background: "#f8f9fa",
          border: "1px solid #dee2e6",
          borderRadius: "0.5rem",
          padding: "1.5rem",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ marginBottom: "1rem", color: "#495057" }}>Admin Tools</h2>
        <div
          className="nav-buttons"
          style={{
            display: "flex",
            gap: "1rem",
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={() => setManagingCategory("Wine")}
            className="nav-button"
            style={{
              background: "#d4af37",
              color: "white",
              border: "none",
              padding: "0.75rem 1.5rem",
              borderRadius: "0.375rem",
              fontSize: "1rem",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            onMouseOver={(e) => (e.target.style.background = "#b8941f")}
            onMouseOut={(e) => (e.target.style.background = "#d4af37")}
            onFocus={(e) => (e.target.style.background = "#b8941f")}
            onBlur={(e) => (e.target.style.background = "#d4af37")}
          >
            🍷 Manage Wines
          </button>
          <button
            onClick={() => setManagingCategory("Spirit")}
            className="nav-button"
            style={{
              background: "#6f42c1",
              color: "white",
              border: "none",
              padding: "0.75rem 1.5rem",
              borderRadius: "0.375rem",
              fontSize: "1rem",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            onMouseOver={(e) => (e.target.style.background = "#5a2d91")}
            onMouseOut={(e) => (e.target.style.background = "#6f42c1")}
            onFocus={(e) => (e.target.style.background = "#5a2d91")}
            onBlur={(e) => (e.target.style.background = "#6f42c1")}
          >
            🥃 Manage Spirits
          </button>
          <button
            onClick={() => setManagingCategory("Cocktail")}
            className="nav-button"
            style={{
              background: "#e83e8c",
              color: "white",
              border: "none",
              padding: "0.75rem 1.5rem",
              borderRadius: "0.375rem",
              fontSize: "1rem",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            onMouseOver={(e) => (e.target.style.background = "#dc226e")}
            onMouseOut={(e) => (e.target.style.background = "#e83e8c")}
            onFocus={(e) => (e.target.style.background = "#dc226e")}
            onBlur={(e) => (e.target.style.background = "#e83e8c")}
          >
            🍸 Manage Cocktails
          </button>
          <button
            onClick={() => setManagingCategory("Mocktail")}
            className="nav-button"
            style={{
              background: "#fd7e14",
              color: "white",
              border: "none",
              padding: "0.75rem 1.5rem",
              borderRadius: "0.375rem",
              fontSize: "1rem",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            onMouseOver={(e) => (e.target.style.background = "#e8650d")}
            onMouseOut={(e) => (e.target.style.background = "#fd7e14")}
            onFocus={(e) => (e.target.style.background = "#e8650d")}
            onBlur={(e) => (e.target.style.background = "#fd7e14")}
          >
            🥤 Manage Mocktails
          </button>
          <button
            onClick={() =>
              navigate(
                currentRestaurant?.slug
                  ? `/restaurant/${currentRestaurant.slug}/menu`
                  : "/drinks"
              )
            }
            className="nav-button"
            style={{
              background: "#28a745",
              color: "white",
              border: "none",
              padding: "0.75rem 1.5rem",
              borderRadius: "0.375rem",
              fontSize: "1rem",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            onMouseOver={(e) => (e.target.style.background = "#218838")}
            onMouseOut={(e) => (e.target.style.background = "#28a745")}
            onFocus={(e) => (e.target.style.background = "#218838")}
            onBlur={(e) => (e.target.style.background = "#28a745")}
          >
            📋 View Public Menu
          </button>
          <button
            onClick={() =>
              navigate(
                currentRestaurant?.slug
                  ? `/restaurant/${currentRestaurant.slug}/compare`
                  : "/compare"
              )
            }
            className="nav-button"
            style={{
              background: "#17a2b8",
              color: "white",
              border: "none",
              padding: "0.75rem 1.5rem",
              borderRadius: "0.375rem",
              fontSize: "1rem",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            onMouseOver={(e) => (e.target.style.background = "#138496")}
            onMouseOut={(e) => (e.target.style.background = "#17a2b8")}
            onFocus={(e) => (e.target.style.background = "#138496")}
            onBlur={(e) => (e.target.style.background = "#17a2b8")}
          >
            🔄 Wine Comparison Tool
          </button>
          <button
            onClick={() => setShowMigrationDashboard(true)}
            className="nav-button"
            style={{
              background: "#fd7e14",
              color: "white",
              border: "none",
              padding: "0.75rem 1.5rem",
              borderRadius: "0.375rem",
              fontSize: "1rem",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            onMouseOver={(e) => (e.target.style.background = "#e8650e")}
            onMouseOut={(e) => (e.target.style.background = "#fd7e14")}
            onFocus={(e) => (e.target.style.background = "#e8650e")}
            onBlur={(e) => (e.target.style.background = "#fd7e14")}
          >
            🚀 Upgrade to AI
          </button>
          <button
            onClick={() => setShowAPIKeyManager(true)}
            className="nav-button"
            style={{
              background: "#6f42c1",
              color: "white",
              border: "none",
              padding: "0.75rem 1.5rem",
              borderRadius: "0.375rem",
              fontSize: "1rem",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            onMouseOver={(e) => (e.target.style.background = "#5a359a")}
            onMouseOut={(e) => (e.target.style.background = "#6f42c1")}
            onFocus={(e) => (e.target.style.background = "#5a359a")}
            onBlur={(e) => (e.target.style.background = "#6f42c1")}
          >
            🔑 API Keys
          </button>
          <button
            onClick={() => setShowPhaseOneTwoDashboard(true)}
            className="nav-button"
            style={{
              background: "#28a745",
              color: "white",
              border: "none",
              padding: "0.75rem 1.5rem",
              borderRadius: "0.375rem",
              fontSize: "1rem",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            onMouseOver={(e) => (e.target.style.background = "#218838")}
            onMouseOut={(e) => (e.target.style.background = "#28a745")}
            onFocus={(e) => (e.target.style.background = "#218838")}
            onBlur={(e) => (e.target.style.background = "#28a745")}
          >
            🎯 Phase 1.2 Setup
          </button>
        </div>

        {/* Phase 2.2: Intelligent Pairing System UI Navigation */}
        <div
          className="pairing-navigation"
          style={{
            background: "#f0f8ff",
            border: "2px solid #4299e1",
            borderRadius: "0.5rem",
            padding: "1.5rem",
            marginBottom: "2rem",
          }}
        >
          <h2 style={{ marginBottom: "1rem", color: "#2b6cb0" }}>
            🍽️ Phase 2.2: Intelligent Pairing System
          </h2>
          <div
            className="pairing-buttons"
            style={{
              display: "flex",
              gap: "1rem",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() =>
                navigate(
                  currentRestaurant?.slug
                    ? `/restaurant/${currentRestaurant.slug}/pairing`
                    : "/pairing"
                )
              }
              className="nav-button"
              style={{
                background: "#4299e1",
                color: "white",
                border: "none",
                padding: "0.75rem 1.5rem",
                borderRadius: "0.375rem",
                fontSize: "1rem",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
              onMouseOver={(e) => (e.target.style.background = "#3182ce")}
              onMouseOut={(e) => (e.target.style.background = "#4299e1")}
              onFocus={(e) => (e.target.style.background = "#3182ce")}
              onBlur={(e) => (e.target.style.background = "#4299e1")}
            >
              🍽️ Pairing Interface
            </button>
            <button
              onClick={() =>
                navigate(
                  currentRestaurant?.slug
                    ? `/restaurant/${currentRestaurant.slug}/pairing/explorer`
                    : "/pairing/explorer"
                )
              }
              className="nav-button"
              style={{
                background: "#48bb78",
                color: "white",
                border: "none",
                padding: "0.75rem 1.5rem",
                borderRadius: "0.375rem",
                fontSize: "1rem",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
              onMouseOver={(e) => (e.target.style.background = "#38a169")}
              onMouseOut={(e) => (e.target.style.background = "#48bb78")}
              onFocus={(e) => (e.target.style.background = "#38a169")}
              onBlur={(e) => (e.target.style.background = "#48bb78")}
            >
              🔍 Pairing Explorer
            </button>
            <button
              onClick={() =>
                navigate(
                  currentRestaurant?.slug
                    ? `/restaurant/${currentRestaurant.slug}/pairing/analytics`
                    : "/pairing/analytics"
                )
              }
              className="nav-button"
              style={{
                background: "#ed8936",
                color: "white",
                border: "none",
                padding: "0.75rem 1.5rem",
                borderRadius: "0.375rem",
                fontSize: "1rem",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
              onMouseOver={(e) => (e.target.style.background = "#dd6b20")}
              onMouseOut={(e) => (e.target.style.background = "#ed8936")}
              onFocus={(e) => (e.target.style.background = "#dd6b20")}
              onBlur={(e) => (e.target.style.background = "#ed8936")}
            >
              📊 Pairing Analytics
            </button>
            <button
              onClick={() =>
                navigate(
                  currentRestaurant?.slug
                    ? `/restaurant/${currentRestaurant.slug}/pairing/feedback`
                    : "/pairing/feedback"
                )
              }
              className="nav-button"
              style={{
                background: "#9f7aea",
                color: "white",
                border: "none",
                padding: "0.75rem 1.5rem",
                borderRadius: "0.375rem",
                fontSize: "1rem",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
              onMouseOver={(e) => (e.target.style.background = "#805ad5")}
              onMouseOut={(e) => (e.target.style.background = "#9f7aea")}
              onFocus={(e) => (e.target.style.background = "#805ad5")}
              onBlur={(e) => (e.target.style.background = "#9f7aea")}
            >
              📝 Pairing Feedback
            </button>
            <button
              onClick={() => navigate("/demo/phase2-pairing")}
              className="nav-button"
              style={{
                background: "#e53e3e",
                color: "white",
                border: "none",
                padding: "0.75rem 1.5rem",
                borderRadius: "0.375rem",
                fontSize: "1rem",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
              onMouseOver={(e) => (e.target.style.background = "#c53030")}
              onMouseOut={(e) => (e.target.style.background = "#e53e3e")}
              onFocus={(e) => (e.target.style.background = "#c53030")}
              onBlur={(e) => (e.target.style.background = "#e53e3e")}
            >
              🚀 Phase 2.2 Demo
            </button>
          </div>
        </div>
      </div>

      <div className="add-drink-form">
        <h2>Add New Drink</h2>
        {/* ... (Add Drink Form remains the same) ... */}
        <form onSubmit={handleAddDrink}>
          <input
            value={newDrink.name}
            onChange={(e) => setNewDrink({ ...newDrink, name: e.target.value })}
            placeholder="Name"
            required
          />
          <input
            value={newDrink.price}
            onChange={(e) =>
              setNewDrink({ ...newDrink, price: e.target.value })
            }
            placeholder="Price"
            type="number"
            required
          />
          <input
            value={newDrink.category}
            onChange={(e) =>
              setNewDrink({ ...newDrink, category: e.target.value })
            }
            placeholder="Category"
            required
          />
          <textarea
            value={newDrink.description}
            onChange={(e) =>
              setNewDrink({ ...newDrink, description: e.target.value })
            }
            placeholder="Description"
          ></textarea>
          <button type="submit">Add Drink</button>
        </form>
      </div>

      <div className="drink-management-list">
        <h2>Manage Drinks</h2>
        {drinks.map((drink) => (
          <div
            key={drink.id}
            className={`drink-manage-item ${
              !drink.inStock ? "out-of-stock" : ""
            }`}
          >
            {/* NEW: Conditional rendering for edit form */}
            {editingDrinkId === drink.id ? (
              <div className="edit-drink-form">
                <input
                  type="number"
                  value={editingDrinkData.price}
                  onChange={(e) =>
                    setEditingDrinkData({
                      ...editingDrinkData,
                      price: e.target.value,
                    })
                  }
                />
                <textarea
                  value={editingDrinkData.description}
                  onChange={(e) =>
                    setEditingDrinkData({
                      ...editingDrinkData,
                      description: e.target.value,
                    })
                  }
                />
                <button
                  onClick={() => handleUpdateDrink(drink.id)}
                  className="save-button"
                >
                  Save
                </button>
                <button onClick={handleCancelEdit}>Cancel</button>
              </div>
            ) : (
              <>
                <span>
                  {drink.name} - ${drink.price} ({drink.category})
                </span>
                <div>
                  <button onClick={() => handleEditClick(drink)}>Edit</button>
                  <button
                    onClick={() => handleUpdateStock(drink.id, drink.inStock)}
                  >
                    {drink.inStock ? "Mark Out of Stock" : "Mark In Stock"}
                  </button>
                  <button
                    onClick={() => handleDeleteDrink(drink.id)}
                    className="delete-button"
                  >
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Category Management Modal */}
      {managingCategory && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              maxWidth: "90vw",
              maxHeight: "90vh",
              overflow: "auto",
              position: "relative",
            }}
          >
            <CategoryManager
              category={managingCategory}
              onClose={() => setManagingCategory(null)}
            />
          </div>
        </div>
      )}

      {/* Migration Dashboard Modal */}
      {showMigrationDashboard && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              maxWidth: "95vw",
              maxHeight: "95vh",
              overflow: "auto",
              position: "relative",
              width: "900px",
            }}
          >
            <div style={{ position: "relative" }}>
              <button
                onClick={() => setShowMigrationDashboard(false)}
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "15px",
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#666",
                  zIndex: 1001,
                }}
                aria-label="Close Migration Dashboard"
              >
                ×
              </button>
              <MigrationDashboard />
            </div>
          </div>
        </div>
      )}

      {/* API Key Manager Modal */}
      {showAPIKeyManager && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: "1000",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "0",
              maxWidth: "90vw",
              maxHeight: "95vh",
              overflow: "auto",
              position: "relative",
              width: "800px",
            }}
          >
            <div style={{ position: "relative" }}>
              <button
                onClick={() => setShowAPIKeyManager(false)}
                style={{
                  position: "absolute",
                  top: "12px",
                  right: "12px",
                  background: "transparent",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#666",
                  zIndex: "1001",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                }}
                aria-label="Close API Key Manager"
              >
                ×
              </button>
              <APIKeyManager
                onKeysUpdated={(keys) => {
                  console.log("API keys updated:", keys);
                  // In a real app, this would save to a secure backend
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Phase 1.2 Dashboard Modal */}
      {showPhaseOneTwoDashboard && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              maxWidth: "95vw",
              maxHeight: "95vh",
              overflow: "auto",
              position: "relative",
              width: "1000px",
            }}
          >
            <div style={{ position: "relative" }}>
              <button
                onClick={() => setShowPhaseOneTwoDashboard(false)}
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "15px",
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#666",
                  zIndex: 1001,
                }}
                aria-label="Close Phase 1.2 Dashboard"
              >
                ×
              </button>
              <PhaseOneTwoDashboard />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
